import { defineComponent, h, onMounted, ref, resolveComponent, watch } from 'vue'
import { RouterLink, useRoute } from 'vue-router'

import {
  CBadge,
  CSidebarNav,
  CNavItem,
  CNavGroup,
  CNavTitle,
} from '@coreui/vue'
import nav from '@/_nav'
import {
  MANAGER_GET_PERMISSIONS,
  NOTIFICATIONS_UNREAD_COUNT_API_HOOK,
  fetch_api_json,
} from '@/common/api_endpoints'

const safeDecodeURI = (value) => {
  try {
    return decodeURI(value)
  } catch (error) {
    // Keep navigation stable even if URL has malformed percent-encoding.
    console.warn('Unable to decode URI path:', value, error)
    return value
  }
}

const normalizePath = (path) =>
  safeDecodeURI(path)
    .replace(/#.*$/, '')
    .replace(/(index)?\.(html)$/, '')

const isActiveLink = (route, link) => {
  if (link === undefined) {
    return false
  }

  if (route.hash === link) {
    return true
  }

  const currentPath = normalizePath(route.path)
  const targetPath = normalizePath(link)
  return currentPath === targetPath
}

const isActiveItem = (route, item) => {
  if (isActiveLink(route, item.to)) {
    return true
  }

  if (item.items) {
    return item.items.some((child) => isActiveItem(route, child))
  }

  return false
}
const CURATION_ITEM_NAME = 'Курирование'
const NOTIFICATIONS_ITEM_NAME = 'Уведомления'

const AppSidebarNav = defineComponent({
  name: 'AppSidebarNav',
  components: {
    CNavItem,
    CNavGroup,
    CNavTitle,
  },
  setup() {
    const route = useRoute()
    const firstRender = ref(true)
    const canViewCuration = ref(false)
    const unreadCount = ref(0)
    const sidebarItems = ref(
      nav.filter((item) => item.name !== CURATION_ITEM_NAME),
    )

    const withUnreadBadge = (items, count) =>
      items.map((item) => {
        if (item.name !== NOTIFICATIONS_ITEM_NAME) {
          return item
        }
        if (!count) {
          const next = { ...item }
          delete next.badge
          return next
        }
        return {
          ...item,
          badge: {
            color: 'danger',
            text: count > 99 ? '99+' : String(count),
          },
        }
      })

    const refreshSidebarItems = () => {
      const base = canViewCuration.value
        ? nav
        : nav.filter((item) => item.name !== CURATION_ITEM_NAME)
      sidebarItems.value = withUnreadBadge(base, unreadCount.value)
    }

    const determinePermissions = async () => {
      try {
        const res = await fetch_api_json(MANAGER_GET_PERMISSIONS, 'POST', {})
        if (!res) {
          canViewCuration.value = false
          refreshSidebarItems()
          return
        }
        const data = await res.json()
        canViewCuration.value = Boolean(data?.success || data?.admin_mod)
        refreshSidebarItems()
      } catch (error) {
        console.error('Unable to determine sidebar permissions:', error)
        canViewCuration.value = false
        refreshSidebarItems()
      }
    }

    const fetchUnreadCount = async () => {
      try {
        const res = await fetch_api_json(NOTIFICATIONS_UNREAD_COUNT_API_HOOK)
        if (!res || !res.ok) {
          unreadCount.value = 0
          refreshSidebarItems()
          return
        }
        const data = await res.json()
        unreadCount.value = Number(data?.count) || 0
        refreshSidebarItems()
      } catch (error) {
        console.error('Unable to load unread notifications count:', error)
        unreadCount.value = 0
        refreshSidebarItems()
      }
    }

    onMounted(() => {
      firstRender.value = false
      determinePermissions()
      fetchUnreadCount()
    })

    watch(
      () => route.path,
      () => {
        fetchUnreadCount()
      },
    )
    const renderItem = (item) => {
      if (item.items) {
        return h(
          CNavGroup,
          {
            ...(firstRender.value && {
              visible: item.items.some((child) => isActiveItem(route, child)),
            }),
          },
          {
            togglerContent: () => [
              h(resolveComponent('CIcon'), {
                customClassName: 'nav-icon',
                name: item.icon,
              }),
              item.name,
            ],
            default: () => item.items.map((child) => renderItem(child)),
          },
        )
      }

      return item.to
        ? h(
            RouterLink,
            {
              to: item.to,
              custom: true,
            },
            {
              default: (props) =>
                h(
                  resolveComponent(item.component),
                  {
                    active: props.isActive,
                    href: props.href,
                    onClick: () => props.navigate(),
                  },
                  {
                    default: () => [
                      item.icon &&
                        h(resolveComponent('CIcon'), {
                          customClassName: 'nav-icon',
                          name: item.icon,
                        }),
                      item.name,
                      item.badge &&
                        h(
                          CBadge,
                          {
                            class: 'ms-auto',
                            color: item.badge.color,
                          },
                          {
                            default: () => item.badge.text,
                          },
                        ),
                    ],
                  },
                ),
            },
          )
        : h(
            resolveComponent(item.component),
            {},
            {
              default: () => item.name,
            },
          )
    }
    return () =>
      h(
        CSidebarNav,
        {},
        {
          default: () => sidebarItems.value.map((item) => renderItem(item)),
        },
      )
  },
})
export { AppSidebarNav }
